import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  FormControl,
  FormControlLabel,
  Button,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  Paper,
  Checkbox,
  Box,
  Autocomplete,
} from "@mui/material";
import AsyncCreatableSelect from "react-select/async-creatable";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ClearIcon from "@mui/icons-material/Clear";
import i18next from "i18next";
import "dayjs/locale/fr";
import dayjs from "dayjs";
import { crmService } from "../../services/crm.service";
import { useSnackbar } from "notistack";

const PREFIX = "MyDesignInfos";

const classes = {
  root: `${PREFIX}-root`,
  imageDesignTitle: `${PREFIX}-imageDesignTitle`,
  fieldsContainer: `${PREFIX}-fieldsContainer`,
  frameTotalStyle: `${PREFIX}-frameTotalStyle`,
  frameOptionsContainer: `${PREFIX}-frameOptionsContainer`,
  optionsFormat: `${PREFIX}-optionsFormat`,
  radioAndDateField: `${PREFIX}-radioAndDateField`,
  radioChoices: `${PREFIX}-radioChoices`,
  tagFieldStyle: `${PREFIX}-tagFieldStyle`,
  addOptionsButton: `${PREFIX}-addOptionsButton`,
  deleteIconSize: `${PREFIX}-deleteIconSize`,
  frameTotalStyleQuantity: `${PREFIX}-frameTotalStyleQuantity`,
};

const StyledDiv = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
    },
    [`& .${classes.imageDesignTitle}`]: {
      color: "black",
      fontSize: "24px",
      maxWidth: "650px",
      width: "100%",
      margin: "0 auto",
      textAlign: "center",
      backgroundColor: theme.palette.primary.main,
    },
    [`& .${classes.fieldsContainer}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-evenly",
      alignContent: "space-around",
      width: "100%",
      maxWidth: "500px",
    },
    [`& .${classes.frameTotalStyleQuantity}`]: {
      [theme.breakpoints.down("sm")]: {
        width: "20%",
      },
      color: "white !important",
      width: "28%",
      "& .MuiOutlinedInput-input": {
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
          WebkitAppearance: "none",
        },
      },
      "& .Mui-error": {
        color: "#FFFFFFB3",
      },
    },
    [`& .${classes.frameTotalStyle}`]: {
      [theme.breakpoints.down("sm")]: {
        width: "27%",
      },
      width: "28%",
    },
    [`& .${classes.optionsFormat}`]: {
      borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
      padding: "0.5rem",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      maxHeight: "350px",
      overflowY: "auto",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
    [`& .${classes.frameOptionsContainer}`]: {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "15px",
      padding: "0.5rem",
    },
    [`& .${classes.radioChoices}`]: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-around",
      marginBottom: "1rem",
    },
    [`& .${classes.radioAndDateField}`]: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      marginBottom: "15px",
    },
    [`& .${classes.tagFieldStyle}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      marginTop: "15px",
      height: "150px",
    },
    [`& .${classes.addOptionsButton}`]: {
      marginBottom: "1rem",
      width: "50%",
      margin: "0 auto",
    },
    [`& .${classes.deleteIconSize}`]: {
      width: "40px",
      height: "40px",
    },

    [`& .react-select__input`]: {
      color: "white !important",
    },
  };
});

const newTab = {
  quantity: "",
  size: "",
  format: "",
};

export default function MyDesignInfos({
  updateData,
  setData,
  data,
  canUserModifyData,
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isLoading = useSelector(
    (state) => state.user.currentUser.accountDetails.loading
  );
  const [debounce, setDebounce] = useState({});
  const [tagsInput, setTagsInput] = useState("");
  const [loadingTags, setLoadingTags] = useState(false);

  const frameTypes = useMemo(
    () => [
      t("Crm.frameTypes.birthday"),
      t("Crm.frameTypes.association"),
      t("Crm.frameTypes.carreer"),
      t("Crm.frameTypes.club"),
      t("Crm.frameTypes.enterprise"),
      t("Crm.frameTypes.event"),
      t("Crm.frameTypes.family"),
      t("Crm.frameTypes.movie"),
      t("Crm.frameTypes.history"),
      t("Crm.frameTypes.one"),
      t("Crm.frameTypes.retired"),
      t("Crm.frameTypes.sponsors"),
      t("Crm.frameTypes.team"),
      t("Crm.frameTypes.trophy"),
      t("Crm.frameTypes.other"),
    ],
    [t]
  );

  useEffect(() => {
    const { cb, delay } = debounce;
    if (cb) {
      const timeout = setTimeout(cb, delay);
      return () => clearTimeout(timeout);
    }
  }, [debounce]);

  function handleDelete(index) {
    setData((data) => {
      data.myDesign.soldFrames.splice(index, 1);
    });
  }

  function handleValidation() {
    let isDisabled = false;

    if (data.myDesign.soldFrames) {
      data.myDesign.soldFrames.forEach((item) => {
        if (
          item.quantity.length === 0 ||
          item.size.length === 0 ||
          item.format.length === 0
        ) {
          isDisabled = true;
        }
      });
    }
    return isDisabled;
  }

  function handleFrameQuantity(eventValue, index) {
    setData((data) => {
      data.myDesign.soldFrames[index].quantity = eventValue;
    });
  }

  function handleSelectFrame(eventValue, index) {
    setData((data) => {
      data.myDesign.soldFrames[index].format = eventValue;
    });
  }

  function handleSelectSize(eventValue, index) {
    setData((data) => {
      data.myDesign.soldFrames[index].size = eventValue;
    });
  }

  const promiseOptions = (value, callback, key) => {
    setLoadingTags(true);
    setDebounce({
      cb: async () => {
        const tagData = await crmService.searchTag(value, key);
        setLoadingTags(false);
        tagData.forEach((item) => {
          item.label = item.name;
          item.value = item.name;
        });
        callback(tagData);
      },
      delay: 500, //ms
    });
  };

  return (
    <StyledDiv>
      <div className={classes.root}>
        <div className={classes.fieldsContainer}>
          <div className={classes.radioAndDateField}>
            <div className={classes.radioChoices}>
              <div>
                <Typography>{t("Crm.client")}</Typography>
                <FormControl>
                  <RadioGroup
                    aria-labelledby="client-prospect-radio-group"
                    value={data.myDesign.type || "prospect"}
                    onChange={(e) =>
                      updateData("myDesign", "type", e.target.value)
                    }
                    name="client-prospect-choice"
                    row
                  >
                    <FormControlLabel
                      value="prospect"
                      control={<Radio />}
                      label="Prospect"
                      disabled={!canUserModifyData}
                    />
                    <FormControlLabel
                      value="client"
                      control={<Radio />}
                      label="Client"
                      disabled={!canUserModifyData}
                    />
                  </RadioGroup>
                </FormControl>
              </div>
              {data.myDesign.type === "client" && (
                <div>
                  <Typography>{t("Crm.typeOfSale")}</Typography>
                  <FormControlLabel
                    value={data.myDesign.transactionFrame}
                    disabled={!canUserModifyData}
                    onChange={(e) => {
                      updateData(
                        "myDesign",
                        "transactionFrame",
                        e.target.checked
                      );
                    }}
                    control={<Checkbox />}
                    label={t("Crm.frame")}
                  />
                  <FormControlLabel
                    value={data.myDesign.transactionAR}
                    disabled={!canUserModifyData}
                    onChange={(e) => {
                      updateData("myDesign", "transactionAR", e.target.checked);
                    }}
                    control={<Checkbox />}
                    label="AR"
                  />
                  <FormControlLabel
                    value={data.myDesign.transactionPoster}
                    disabled={!canUserModifyData}
                    onChange={(e) => {
                      updateData(
                        "myDesign",
                        "transactionPoster",
                        e.target.checked
                      );
                    }}
                    control={<Checkbox />}
                    label={t("Crm.poster")}
                  />
                </div>
              )}
            </div>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={i18next.language.substring(0, 2)}
            >
              <DatePicker
                disableFuture
                disabled={!canUserModifyData}
                label={t("Crm.transactionDate")}
                onChange={(e) =>
                  updateData(
                    "myDesign",
                    "soldDate",
                    e ? dayjs(e).format("YYYY-MM-DD") : null
                  )
                }
                value={
                  data.myDesign.soldDate ? dayjs(data.myDesign.soldDate) : null
                }
              />
            </LocalizationProvider>
          </div>
          {data.myDesign.type === "client" &&
            data.myDesign.transactionFrame && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={handleValidation() || !canUserModifyData}
                  className={classes.addOptionsButton}
                  onClick={() => {
                    setData((data) => {
                      data.myDesign.soldFrames.push(newTab);
                    });
                  }}
                >
                  {t("Crm.buttons.add")}
                </Button>
                <div className={classes.optionsFormat}>
                  {data.myDesign.soldFrames.map((item, index) => (
                    <Paper
                      elevation={20}
                      className={classes.frameOptionsContainer}
                      key={`myDesign-size-and-molding-${index + 1}`}
                    >
                      <TextField
                        className={classes.frameTotalStyleQuantity}
                        type="number"
                        disabled={!canUserModifyData}
                        InputProps={{ inputProps: { min: 0 } }}
                        variant="outlined"
                        label={t("Crm.quantity")}
                        value={item.quantity ? item.quantity : ""}
                        error={!item.quantity}
                        onKeyDown={(e) => {
                          if (
                            e.key === "e" ||
                            e.key === "E" ||
                            e.key === "-" ||
                            e.key === "+"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          handleFrameQuantity(e.target.value, index);
                        }}
                      />
                      <FormControl className={classes.frameTotalStyle}>
                        <InputLabel id="label-size">{t("Crm.size")}</InputLabel>
                        <Select
                          labelId="label-size"
                          disabled={!canUserModifyData}
                          id="size-select"
                          value={item.size ? item.size : ""}
                          error={!item.size}
                          label={t("Crm.size")}
                          onChange={(e) =>
                            handleSelectSize(e.target.value, index)
                          }
                        >
                          <MenuItem value={"l"}>L</MenuItem>
                          <MenuItem value={"xl"}>XL</MenuItem>
                          <MenuItem value={"xxl"}>XXL</MenuItem>
                          <MenuItem value={"custom"}>Custom</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl className={classes.frameTotalStyle}>
                        <InputLabel id="label-frame">
                          {t("Crm.frame")}
                        </InputLabel>
                        <Select
                          labelId="label-frame"
                          disabled={!canUserModifyData}
                          id="frame-select"
                          value={item.format ? item.format : ""}
                          error={!item.format}
                          label={t("Crm.frame")}
                          onChange={(e) =>
                            handleSelectFrame(e.target.value, index)
                          }
                        >
                          <MenuItem value={"design"}>Design</MenuItem>
                          <MenuItem value={"oak"}>
                            {t("Crm.frameChoices.oak")}
                          </MenuItem>
                          <MenuItem value={"ipe"}>
                            {t("Crm.frameChoices.ipe")}
                          </MenuItem>
                          <MenuItem value={"white"}>
                            {t("Crm.frameChoices.white")}
                          </MenuItem>
                          <MenuItem value={"versailles"}>Versailles</MenuItem>
                        </Select>
                      </FormControl>
                      <IconButton
                        variant="outlined"
                        disabled={!canUserModifyData}
                        onClick={() => handleDelete(index)}
                        className={classes.deleteIconSize}
                      >
                        <ClearIcon />
                      </IconButton>
                    </Paper>
                  ))}
                </div>
              </>
            )}
          <div className={classes.tagFieldStyle}>
            <Autocomplete
              id="frame-type-autocomplete"
              disabled={!canUserModifyData}
              options={frameTypes}
              disableClearable
              onChange={(e, selected) => {
                updateData("myDesign", "frameType", selected);
              }}
              value={data.myDesign.frameType}
              multiple
              // noOptionsText={
              //   <Box
              //     display="flex"
              //     justifyContent="space-between"
              //     alignItems="center"
              //   >
              //     {t("configurator.nationalityNotFound")}
              //     <Button
              //       variant="outlined"
              //       color="primary"
              //       onClick={() => {
              //         console.log("Add nationality TBD");
              //       }}
              //     >
              //       {t("configurator.createNationality")}
              //     </Button>
              //   </Box>
              // }
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Crm.frameTypeTitle")}
                  variant="outlined"
                />
              )}
            />
            <AsyncCreatableSelect
              isClearable
              value={data.myDesign.activity.map((item) => {
                return {
                  ...item,
                  value: item.name,
                  label: item.name,
                };
              })}
              isMulti
              isDisabled={isLoading || !canUserModifyData}
              cacheOptions
              loadOptions={(value, callback) =>
                promiseOptions(value, callback, "activity")
              }
              loadingMessage={() => t("account.form.loading")}
              formatCreateLabel={(inputValue) =>
                `${t("account.form.create")} ${inputValue}`
              }
              noOptionsMessage={() => {
                return null;
              }}
              isLoading={loadingTags}
              getOptionLabel={(option) =>
                option.label ? option.label : option.name
              }
              placeholder={t("Crm.tags.activity")}
              onKeyDown={(e) => {
                if (!tagsInput) return;
                switch (e.key) {
                  case "Enter":
                    setTagsInput("");
                    break;
                  case "Tab":
                    setTagsInput("");
                    break;
                  default:
                    setTagsInput("");
                    break;
                }
              }}
              onInputChange={(value) => setTagsInput(value)}
              onChange={(newValue, actionMeta) => {
                if (
                  actionMeta.action !== "pop-value" &&
                  actionMeta.action !== "remove-value" &&
                  actionMeta.action !== "clear"
                ) {
                  if (actionMeta.action === "create-option") {
                    crmService
                      .createTag("activity", tagsInput)
                      .then(() =>
                        enqueueSnackbar(t("Crm.toasts.createSuccess"), {
                          variant: "success",
                        })
                      )
                      .catch((err) => {
                        if (err === 403)
                          enqueueSnackbar(t("toasts.unauthorizedError"), {
                            variant: "error",
                          });
                        else
                          enqueueSnackbar(t("Crm.toasts.createFail"), {
                            variant: "error",
                          });
                      });
                  }
                  let tmpValue = [];
                  newValue.forEach((item) => {
                    tmpValue.push({
                      ...item,
                      label: item.value,
                      name: item.value,
                    });
                  });
                  updateData("myDesign", "activity", tmpValue);
                } else {
                  updateData("myDesign", "activity", !newValue ? [] : newValue);
                }
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
            <AsyncCreatableSelect
              isClearable
              value={data.myDesign.club.map((item) => {
                return {
                  ...item,
                  value: item.name,
                  label: item.name,
                };
              })}
              isMulti
              isDisabled={isLoading || !canUserModifyData}
              cacheOptions
              loadOptions={(value, callback) =>
                promiseOptions(value, callback, "brand")
              }
              loadingMessage={() => t("account.form.loading")}
              formatCreateLabel={(inputValue) =>
                `${t("account.form.create")} ${inputValue}`
              }
              noOptionsMessage={() => {
                return null;
              }}
              isLoading={loadingTags}
              getOptionLabel={(option) =>
                option.label ? option.label : option.name
              }
              placeholder={t("Crm.tags.clubBrand")}
              onKeyDown={(e) => {
                if (!tagsInput) return;
                switch (e.key) {
                  case "Enter":
                    setTagsInput("");
                    break;
                  case "Tab":
                    setTagsInput("");
                    break;
                  default:
                    setTagsInput("");
                    break;
                }
              }}
              onInputChange={(value) => setTagsInput(value)}
              onChange={(newValue, actionMeta) => {
                if (
                  actionMeta.action !== "pop-value" &&
                  actionMeta.action !== "remove-value" &&
                  actionMeta.action !== "clear"
                ) {
                  if (actionMeta.action === "create-option") {
                    crmService
                      .createTag("brand", tagsInput)
                      .then(() =>
                        enqueueSnackbar(t("Crm.toasts.createSuccess"), {
                          variant: "success",
                        })
                      )
                      .catch((err) => {
                        if (err === 403)
                          enqueueSnackbar(t("toasts.unauthorizedError"), {
                            variant: "error",
                          });
                        else
                          enqueueSnackbar(t("Crm.toasts.createFail"), {
                            variant: "error",
                          });
                      });
                  }
                  let tmpValue = [];
                  newValue.forEach((item) => {
                    tmpValue.push({
                      ...item,
                      label: item.value,
                      name: item.value,
                    });
                  });
                  updateData("myDesign", "club", tmpValue);
                } else {
                  updateData("myDesign", "club", !newValue ? [] : newValue);
                }
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        </div>
      </div>
    </StyledDiv>
  );
}
